import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import validation from "./validation";
import { CreateEntry, UpdateEntry } from "../../providers/dataProvider";
import { CheckNull } from "../../utils/Validate";
import { SuccessMessages, ErrorMessages } from "../../constants/MessageConstants";

const useForm = (facilityData, countrydata) => {
  const location = useLocation();
  const navigate = useNavigate();
  const userProfile = JSON.parse(localStorage.getItem("userProfile"));
  const normalizedFacilityData = Array.isArray(facilityData)
    ? facilityData
    : facilityData?.data || facilityData?.data?.data || [];
  const [formValues, setFormValues] = useState({
    facilityCode: "",
    facilityName: "",
    address: "",
    countryId: 0,
    isDefault: false,
    isActive: true,
    companyId: location?.state?.companyId || userProfile?.userCompanyId || "",
  });
  const [country, setCountry] = useState(null);
  const [company, setCompany] = useState(null);
  const [formErrors, setFormError] = useState({});
  const [isSubmit, setIsSubmit] = useState(false);
  const [createMsg, setCreateMsg] = useState(null);
  const [show, setShow] = useState(false);
  const [errorShow, setErrorShow] = useState({
    show: false,
    errorMessage: "",
  });
  const [editFacility, setEditFacility] = useState(null);

  useEffect(() => {
    const state = location?.state || {};
    const resolvedCompanyId =
      state?.companyId || state?.row?.companyId || userProfile?.userCompanyId || "";

    if (!resolvedCompanyId) {
      navigate("/company-master");
      return;
    }

    if (state?.id && state?.canEdit !== true) {
      navigate("/dashboard");
      return;
    }

    if (!state?.id && state?.canAdd === false) {
      navigate("/dashboard");
      return;
    }

    setFormValues((prev) => ({
      ...prev,
      companyId: resolvedCompanyId,
    }));

    if (state?.row) {
      setEditFacility(state.row);
    }
  }, [location?.state, navigate, userProfile?.userCompanyId]);

  useEffect(() => {
    if (!editFacility) return;

    const countries = Array.isArray(countrydata)
      ? countrydata
      : countrydata?.data || countrydata?.data?.data || [];
    const selectedCountry = countries.find(
      (item) => item?.id === editFacility?.countryId
    );

    setFormValues({
      id: editFacility?.id,
      facilityCode: editFacility?.facilityCode || "",
      facilityName: editFacility?.facilityName || "",
      address: editFacility?.address || "",
      countryId: editFacility?.countryId || 0,
      isDefault: !!editFacility?.isDefault,
      isActive: editFacility?.isActive !== false,
      companyId:
        editFacility?.companyId ||
        location?.state?.companyId ||
        userProfile?.userCompanyId ||
        "",
    });
    setCountry(
      selectedCountry
        ? { value: selectedCountry.id, label: selectedCountry.name, ...selectedCountry }
        : null
    );
  }, [editFacility, countrydata, location?.state, userProfile?.userCompanyId]);

  const HandleFormChange = (e) => {
    const { name, type, checked, value } = e.target;

    setFormValues((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    const errors = validation(
      {
        ...formValues,
        [name]: type === "checkbox" ? checked : value,
      },
      normalizedFacilityData
    );

    setFormError((prev) => ({
      ...prev,
      [name]: errors?.[name],
    }));
  };

  const HandleCountryChange = (selectedOption) => {
    setCountry(selectedOption);
    setFormValues((prev) => ({
      ...prev,
      countryId: selectedOption?.value || 0,
    }));

    const errors = validation(
      {
        ...formValues,
        countryId: selectedOption?.value || 0,
      },
      normalizedFacilityData
    );

    setFormError((prev) => ({
      ...prev,
      country: errors?.country,
    }));
  };

  const HandleCompanyChange = (selectedOption) => {
    setCompany(selectedOption);
    setFormValues((prev) => ({
      ...prev,
      companyId: selectedOption?.value || "",
    }));

    const errors = validation(
      {
        ...formValues,
        companyId: selectedOption?.value || "",
      },
      normalizedFacilityData
    );

    setFormError((prev) => ({
      ...prev,
      company: errors?.company,
    }));
  };

  const HandleFormSubmit = (event) => {
    event.preventDefault();
    setFormError(validation(formValues, normalizedFacilityData));
    setIsSubmit(true);
  };

  useEffect(() => {
    if (Object.keys(formErrors).length !== 0 || !isSubmit) {
      setIsSubmit(false);
      return;
    }

    const payload = {
      ...formValues,
      companyId:
        formValues?.companyId ||
        location?.state?.companyId ||
        userProfile?.userCompanyId ||
        "",
    };

    if (CheckNull(formValues?.id) !== "") {
      UpdateEntry(
        "manufacturingFacility",
        payload,
        setCreateMsg,
        SuccessMessages.UPDATED("Facility"),
        ErrorMessages.UPDATE_ERROR("Facility")
      );
    } else {
      CreateEntry(
        "manufacturingFacility",
        payload,
        setCreateMsg,
        SuccessMessages.CREATED("Facility"),
        ErrorMessages.CREATE_ERROR("Facility")
      );
    }

    setIsSubmit(false);
  }, [formErrors, isSubmit, formValues, location?.state, userProfile?.userCompanyId]);

  useEffect(() => {
    if (!createMsg?.message) return;

    if (CheckNull(createMsg?.message).includes("Failed")) {
      setErrorShow({
        show: true,
        errorMessage: createMsg?.successData,
      });
      return;
    }

    setShow(true);
  }, [createMsg]);

  const ClearFormValues = () => {
    navigate("/facility-master", {
      state: {
        companyId:
          formValues?.companyId ||
          location?.state?.companyId ||
          userProfile?.userCompanyId ||
          "",
      },
    });
  };

  const SuccessDoneClick = () => {
    setShow(false);
    navigate("/facility-master", {
      state: {
        companyId:
          formValues?.companyId ||
          location?.state?.companyId ||
          userProfile?.userCompanyId ||
          "",
      },
    });
  };

  const ErrorDoneClick = () => {
    setErrorShow({
      show: false,
      errorMessage: "",
    });
    setCreateMsg(null);
  };

  return {
    HandleFormChange,
    formValues,
    setFormValues,
    country,
    company,
    HandleCompanyChange,
    HandleCountryChange,
    HandleFormSubmit,
    formErrors,
    createMsg,
    show,
    setShow,
    errorShow,
    SuccessDoneClick,
    ErrorDoneClick,
    ClearFormValues,
    setCountry,
    setCompany,
    setFormError,
  };
};

export default useForm;
